import type { ApiErrorBody } from "@agp/shared";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3001";

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method: string;
  path: string;
  body?: unknown;
  authHeader: string | null;
  signal?: AbortSignal;
}

async function request<T>(opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.authHeader) headers.Authorization = opts.authHeader;

  const res = await fetch(`${BASE_URL}${opts.path}`, {
    method: opts.method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: "include",
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { error: "parse_error", message: text };
    }
  }

  if (!res.ok) {
    const body = parsed as ApiErrorBody | null;
    throw new ApiError(res.status, body, body?.message ?? res.statusText);
  }
  return parsed as T;
}

export type AuthHeaderGetter = () => Promise<string | null>;

export interface Api {
  get: <T>(path: string, signal?: AbortSignal) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  delete: <T>(path: string) => Promise<T>;
  /** POST raw binary (file upload endpoints); returns parsed JSON. */
  postBinary: <T>(path: string, data: Blob, contentType: string) => Promise<T>;
  /** POST expecting a binary response (sample DOCX download). */
  postForBlob: (path: string) => Promise<{ blob: Blob; filename: string | null }>;
}

export function makeApi(getAuthHeader: AuthHeaderGetter): Api {
  return {
    get: async <T>(path: string, signal?: AbortSignal): Promise<T> =>
      request<T>({ method: "GET", path, authHeader: await getAuthHeader(), signal }),
    post: async <T>(path: string, body?: unknown): Promise<T> =>
      request<T>({ method: "POST", path, body, authHeader: await getAuthHeader() }),
    patch: async <T>(path: string, body?: unknown): Promise<T> =>
      request<T>({ method: "PATCH", path, body, authHeader: await getAuthHeader() }),
    delete: async <T>(path: string): Promise<T> =>
      request<T>({ method: "DELETE", path, authHeader: await getAuthHeader() }),
    postBinary: async <T>(path: string, data: Blob, contentType: string): Promise<T> => {
      const authHeader = await getAuthHeader();
      const res = await fetch(`${BASE_URL}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": contentType,
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: data,
        credentials: "include",
      });
      const text = await res.text();
      const parsed = text ? (JSON.parse(text) as unknown) : null;
      if (!res.ok) {
        const body = parsed as ApiErrorBody | null;
        throw new ApiError(res.status, body, body?.message ?? res.statusText);
      }
      return parsed as T;
    },
    postForBlob: async (path: string) => {
      const authHeader = await getAuthHeader();
      const res = await fetch(`${BASE_URL}${path}`, {
        method: "POST",
        headers: authHeader ? { Authorization: authHeader } : {},
        credentials: "include",
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new ApiError(res.status, null, text || res.statusText);
      }
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
      return { blob: await res.blob(), filename };
    },
  };
}

/** Raw binary PUT for the presigned upload URL (S3 or the dev stub). */
export async function uploadFile(url: string, file: File, contentType = file.type): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`upload failed (${res.status})`);
  }
}
